import { ControlledTextField } from '@if/mui'
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import AsyncActionButton from '@src/shared/components/AsyncActionButton'
import { useBulkIgnoreExceptions } from '@src/tools/error-tracker/api/exceptions-api'
import { reportErrorToConsole } from '@src/shared/services/error-logger'
import { BulkIgnoreRequestDto } from '@src/tools/error-tracker/types/BulkActions'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface BulkIgnoreFormData {
  note: string
}

interface BulkIgnoreDialogProps {
  selectedIds: string[]
  open: boolean
  onSuccess: () => void
  onCancel: () => void
}

const BulkIgnoreDialog = ({ selectedIds, open, onSuccess, onCancel }: BulkIgnoreDialogProps) => {
  const { t } = useTranslation()
  const [bulkIgnoreExceptions, isIgnoring, ignoreError, resetIgnore] = useBulkIgnoreExceptions()

  const { control, handleSubmit, reset } = useForm<BulkIgnoreFormData>({
    defaultValues: {
      note: '',
    },
  })

  useEffect(() => {
    reset({
      note: '',
    })
    resetIgnore()
  }, [open, reset, resetIgnore])

  const onSubmit = async (formData: BulkIgnoreFormData) => {
    try {
      const request: BulkIgnoreRequestDto = {
        exceptionIds: selectedIds,
        note: formData.note.trim() || undefined,
      }
      await bulkIgnoreExceptions(request)
      onSuccess()
    } catch (err) {
      // Error is handled by react-query and shown via ignoreError
      reportErrorToConsole(err)
    }
  }

  const handleCancel = () => {
    reset()
    resetIgnore()
    onCancel()
  }

  const selectedCount = selectedIds.length

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {t('dialogs.bulkIgnore.title', 'Ignore Selected Items')}
        </DialogTitle>
        <DialogContent>
          {ignoreError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('dialogs.bulkIgnore.error', 'An error occurred while ignoring the selected items. Please try again.')}
            </Alert>
          )}
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('dialogs.bulkIgnore.confirmMessage', {
              defaultValue: 'Are you sure you want to ignore {{count}} selected {{items}}?',
              count: selectedCount,
              items: selectedCount === 1 ? 'item' : 'items'
            })}
          </Typography>

          <ControlledTextField
            name="note"
            control={control}
            label={t('dialogs.bulkIgnore.noteLabel', 'Note (Optional)')}
            placeholder={t('dialogs.bulkIgnore.notePlaceholder', 'Add an optional note about why these items are being ignored...')}
            multiline
            rows={3}
            fullWidth
            disabled={isIgnoring}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <AsyncActionButton
            variant="outlined"
            onClick={handleCancel}
            disabled={isIgnoring}
            type="button"
          >
            {t('common.cancel', 'Cancel')}
          </AsyncActionButton>
          <AsyncActionButton
            variant="contained"
            color="warning"
            isPending={isIgnoring}
            disabled={isIgnoring || selectedCount === 0}
            type="submit"
          >
            {t('dialogs.bulkIgnore.applyButton', 'Ignore {{count}} {{items}}', {
              count: selectedCount,
              items: selectedCount === 1 ? 'Item' : 'Items'
            })}
          </AsyncActionButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default BulkIgnoreDialog